import {
  nowIso,
  type AnnotationRecord,
  type DocumentRecord,
  type ReadingStateRecord,
} from "@/domain";
import type { MarginaliaDatabase } from "@/storage";

import {
  createDocumentExportV1,
  type BuildDocumentExportOptions,
} from "./export-document";
import {
  marginaliaLibraryExportV1Schema,
  type MarginaliaLibraryExportV1,
} from "./schemas/v1";

export type BuildLibraryExportOptions = BuildDocumentExportOptions;

function compareStrings(left: string, right: string): number {
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

function compareDocuments(left: DocumentRecord, right: DocumentRecord): number {
  return (
    compareStrings(left.sha256, right.sha256) ||
    compareStrings(left.fileName, right.fileName) ||
    compareStrings(left.id, right.id)
  );
}

export function createLibraryExportV1(
  documents: readonly DocumentRecord[],
  annotations: readonly AnnotationRecord[],
  readingStates: readonly ReadingStateRecord[],
  options: BuildLibraryExportOptions,
): MarginaliaLibraryExportV1 {
  const exportedAt = options.exportedAt ?? nowIso();
  const annotationsByDocument = new Map<string, AnnotationRecord[]>();
  for (const annotation of annotations) {
    const records = annotationsByDocument.get(annotation.documentId) ?? [];
    records.push(annotation);
    annotationsByDocument.set(annotation.documentId, records);
  }
  const readingStateByDocument = new Map(
    readingStates.map((readingState) => [
      readingState.documentId,
      readingState,
    ]),
  );

  const bundle: MarginaliaLibraryExportV1 = {
    format: "marginalia-library",
    formatVersion: 1,
    applicationVersion: options.applicationVersion,
    exportedAt,
    documents: [...documents].sort(compareDocuments).map((document) => {
      const documentBundle = createDocumentExportV1(
        document,
        annotationsByDocument.get(document.id) ?? [],
        readingStateByDocument.get(document.id),
        {
          applicationVersion: options.applicationVersion,
          exportedAt,
        },
      );
      return {
        document: documentBundle.document,
        annotations: documentBundle.annotations,
        readingState: documentBundle.readingState,
      };
    }),
  };

  return marginaliaLibraryExportV1Schema.parse(bundle);
}

export async function buildLibraryExport(
  db: MarginaliaDatabase,
  options: BuildLibraryExportOptions,
): Promise<MarginaliaLibraryExportV1> {
  return db.transaction(
    "r",
    [db.documents, db.annotations, db.readingStates],
    async () => {
      const [documents, annotations, readingStates] = await Promise.all([
        db.documents.toArray(),
        db.annotations.toArray(),
        db.readingStates.toArray(),
      ]);
      return createLibraryExportV1(
        documents,
        annotations,
        readingStates,
        options,
      );
    },
  );
}

export function serializeLibraryExport(
  bundle: MarginaliaLibraryExportV1,
): string {
  const validated = marginaliaLibraryExportV1Schema.parse(bundle);
  return `${JSON.stringify(validated, null, 2)}\n`;
}
